import { useCallback, useEffect, useState } from "react";
import { FUNCTIONS_BASE_URL, getAuthToken } from "../services/firebase";
import { AgentChatInfo } from "../types";
import { useConnectionMonitor } from "./useConnectionMonitor";

export const useRealtimeAgents = () => {
  const [agents, setAgents] = useState<AgentChatInfo[]>([]);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Connection monitoring
  const connectionMonitor = useConnectionMonitor({
    onConnectionLost: () => {
      console.warn("Agents connection lost, attempting to reconnect...");
    },
    onConnectionRestored: () => {
      console.log("Agents connection restored");
    },
    onMaxReconnectAttemptsReached: () => {
      console.error("Max reconnection attempts reached for agents");
      // Trigger a full refresh
      setRefreshTrigger((prev) => prev + 1);
    },
  });

  useEffect(() => {
    const connectToStream = async () => {
      // Close existing connection if any
      if (eventSource) {
        eventSource.close();
      }

      const token = await getAuthToken();
      if (!token) {
        console.error("No authentication token available");
        return;
      }

      const newEventSource = new EventSource(`${FUNCTIONS_BASE_URL}/agentListStream?token=${encodeURIComponent(token)}`);
      setEventSource(newEventSource);

      newEventSource.onopen = () => {
        connectionMonitor.handleConnectionOpen();
      };

      newEventSource.onerror = () => {
        connectionMonitor.handleConnectionError();
      };

      newEventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case "agent_list":
            setAgents(data.agents);
            break;

          case "ping":
            // Handle ping from server
            connectionMonitor.handlePing();
            break;
        }
      };

      return () => {
        newEventSource.close();
        setEventSource(null);
      };
    };

    connectToStream();
  }, [refreshTrigger]);

  // Refresh function
  const refresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return {
    agents,
    isConnected: connectionMonitor.status.isConnected,
    refresh,
  };
};
